import { Injectable, SecurityContext, inject } from '@angular/core';
import { MatomoTracker } from 'ngx-matomo-client';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { LandingConfig, LandingProduct } from '../../models/program-product-config.model';
import { ContentService } from '../../services/content.service';
import { DataService } from '../../services/data.service';
import { QuestionnaireService } from '../../services/questionnaire.service';
import { WorkflowService } from '../../services/workflow.service';
import { FormattingService } from '../../services/formatting.service';
import { UserType } from '../../models/enums/user-type.enum';
import { GetEntryRequest } from '../../models/content/get-entry-request.model';
import { ContentType } from '../../models/enums/content-type';
import { FeatureContent } from '../../models/content/feature-content.model';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class LandingService {
  private _contentService = inject(ContentService);
  private _dataService = inject(DataService);
  private _questionnaireService = inject(QuestionnaireService);
  private _matomoTracker = inject(MatomoTracker);
  private _workflowService = inject(WorkflowService);
  private _formattingService = inject(FormattingService);
   private _sanitizer = inject(DomSanitizer);

  getContent(): Observable<FeatureContent> {
    const request: GetEntryRequest = {
      ...this._dataService.getEntryRequest$(),
      contentType: ContentType.depFeature,
      formName: 'landing',
    };
    return this._contentService.getEntry(request);
  }

  getLandingConfig(): Observable<LandingConfig> {
    return of(environment.LANDING_CONFIG);
  }

  setProducts(products: LandingProduct[]) {
    this._matomoTracker.trackEvent('product', 'set product count', 'welcome page product count', products.length);
    this._dataService.setInitialDrugFamily(products);
    this._dataService.setProducts(products.map(product => product.display));
  }

  sanitizeText(product: string): string{
    return this._sanitizer.sanitize(SecurityContext.HTML, product) ?? '';
  }

  setWorkflow(userType: string): void {
    this._matomoTracker.trackEvent('user-type', 'set user type', 'welcome page role', userType.includes('Prescriber') ? 1 : 0);
    this._workflowService.setWorkflow(userType);
    switch (userType) {
      case 'Patient Initiated':
        this._workflowService.initialUserType$.set(UserType.patientInitiated);
        break;
      default:
        this._workflowService.initialUserType$.set(UserType.prescriberInitiated);
    }
  }

  hideProgressTracker(): void {
    this._workflowService.showOrHideProgressTracker(false);
  }

  hideProgressFlow(): void {
    this._workflowService.showOrHideProgressLine(false);
  }

  hidePatientSummary(): void {
    this._workflowService.showOrHidePatientSummary(false);
  }

  createEnrollmentId(): void {
    this._dataService.createEnrollmentId();
  }

  clearSession(): void {
    this._dataService.clearSession();
    this._questionnaireService.clearQuestionnaire();
    this._workflowService.resetCurrentStep();
    this._workflowService.resetWorkflowProgress();
  }
}
